import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AlertCircle,
  Building,
  Calendar,
  FileText,
  Gauge,
  History,
  Info,
  Loader2,
  MapPin,
  Pencil,
  QrCode,
  ShieldAlert,
  Users,
  Wrench,
  X,
} from 'lucide-react';
import type {
  EquipementCriticite,
  EquipementStatut,
  EquipementStatutAffectation,
  EquipementType,
} from '@mygmao/shared';
import { api, extractErrorMessage } from '../../lib/api';
import { ModalPortal } from '../ModalPortal';
import { HistoriqueTimeline } from '../batiments/HistoriqueTimeline';
import { DocumentsList } from '../batiments/DocumentsList';
import { EquipementFormModal } from './EquipementFormModal';
import { AffectationsPanel } from './AffectationsPanel';
import { MaintenancesPanel } from './MaintenancesPanel';
import { EtalonnagesPanel } from './EtalonnagesPanel';
import { QrLabelPanel } from './QrLabelPanel';
import { PdfExportButton } from '../shared/PdfExportButton';
import { usePermissions } from '../../hooks/usePermissions';
import { StatutEquipementBadge, CriticiteBadge } from './badges';
import { ServiceDetailDrawer } from '../services/ServiceDetailDrawer';
import { BatimentDetailDrawer } from '../batiments/BatimentDetailDrawer';

/**
 * Drawer détail équipement 360° (US-EQ-02 + 06 + 07 + 08 + 09 + 10 + 11).
 *
 *  Onglets : Informations · Affectations · Maintenance · Métrologie ·
 *  Documents · QR · Historique.
 *
 *  Bouton "Modifier" en haut + actions contextuelles dans l'onglet
 *  Informations (demander affectation utilisateur si non affecté).
 */

interface EquipementDetail {
  id: string;
  tenant_id: string;
  code_equipement: string;
  numero_inventaire: string | null;
  code_barres: string | null;
  numero_serie: string | null;
  designation: string;
  type: EquipementType;
  statut: EquipementStatut;
  criticite: EquipementCriticite | null;
  fabricant: string | null;
  modele: string | null;
  service_id: string | null;
  batiment_id: string | null;
  etage_id: string | null;
  salle_id: string | null;
  responsable_id: string | null;
  utilisateur_affecte_id: string | null;
  statut_affectation: EquipementStatutAffectation;
  est_mobile: boolean;
  custom_fields: Record<string, unknown>;
  date_demande_affectation: string | null;
  date_reponse_affectation: string | null;
  motif_refus: string | null;
  created_at: string;
  updated_at: string;
  service: { id: string; code: string; nom_complet: string } | null;
  batiment: { id: string; code: string; nom: string } | null;
  etage: { id: string; code: string; numero: string; nom: string | null } | null;
  salle: { id: string; code: string; nom: string } | null;
  responsable: {
    id: string;
    first_name: string;
    last_name: string;
    email: string;
  } | null;
  utilisateur_affecte: {
    id: string;
    first_name: string;
    last_name: string;
    email: string;
  } | null;
}

type Tab =
  | 'info'
  | 'affectations'
  | 'maintenance'
  | 'metrologie'
  | 'documents'
  | 'qr'
  | 'historique';

export function EquipementDetailDrawer({
  equipementId,
  onClose,
  onRefresh,
}: {
  equipementId: string;
  onClose(): void;
  onRefresh?(): void;
}) {
  const { can } = usePermissions();
  const canEdit = can('equipements', 'update');
  const navigate = useNavigate();

  const [detail, setDetail] = useState<EquipementDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [tab, setTab] = useState<Tab>('info');
  const [editOpen, setEditOpen] = useState(false);
  const [askUserOpen, setAskUserOpen] = useState(false);
  const [openServiceId, setOpenServiceId] = useState<string | null>(null);
  const [openBatimentId, setOpenBatimentId] = useState<string | null>(null);
  const pdfRef = useRef<HTMLDivElement>(null);

  async function refresh() {
    setLoading(true);
    try {
      const res = await api.get<{ data: EquipementDetail }>(`/equipements/${equipementId}`);
      setDetail(res.data.data);
    } catch (err) {
      setErrorMessage(extractErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [equipementId]);

  return (
    <ModalPortal>
      <div className="fixed inset-0 bg-black bg-opacity-30 z-40" onClick={onClose} aria-hidden />
      <aside className="fixed top-0 right-0 z-50 h-screen w-full max-w-2xl bg-white shadow-2xl overflow-hidden flex flex-col">
        <div className="flex items-center justify-between p-4 border-b border-gray-200 flex-shrink-0">
          <h2 className="text-lg font-semibold text-gray-900 flex items-center min-w-0">
            <Wrench className="w-5 h-5 mr-2 text-primary-600 flex-shrink-0" />
            <span className="truncate">{detail?.designation ?? 'Détail équipement'}</span>
            {detail && (
              <span className="ml-2 text-xs font-medium text-gray-500 uppercase tracking-wide flex-shrink-0">
                {detail.code_equipement}
              </span>
            )}
          </h2>
          <div className="flex items-center space-x-1">
            {detail && (
              <PdfExportButton
                targetRef={pdfRef}
                filename={`fiche-equipement-${detail.code_equipement}.pdf`}
                label="PDF fiche"
                onBeforeExport={() => setTab('info')}
              />
            )}
            {canEdit && (
              <button
                onClick={() => setEditOpen(true)}
                className="inline-flex items-center px-3 py-1.5 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50"
              >
                <Pencil className="w-3.5 h-3.5 mr-1" />
                Modifier
              </button>
            )}
            <button
              onClick={onClose}
              className="p-1 rounded-lg hover:bg-gray-100"
              aria-label="Fermer"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>
        </div>

        <div className="border-b border-gray-200 flex-shrink-0 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          <nav className="flex">
            <TabButton active={tab === 'info'} onClick={() => setTab('info')}>
              <Info className="w-3.5 h-3.5 inline mr-1" />
              Informations
            </TabButton>
            <TabButton active={tab === 'affectations'} onClick={() => setTab('affectations')}>
              <Users className="w-3.5 h-3.5 inline mr-1" />
              Affectations
            </TabButton>
            <TabButton active={tab === 'maintenance'} onClick={() => setTab('maintenance')}>
              <Wrench className="w-3.5 h-3.5 inline mr-1" />
              Maintenance
            </TabButton>
            <TabButton active={tab === 'metrologie'} onClick={() => setTab('metrologie')}>
              <Gauge className="w-3.5 h-3.5 inline mr-1" />
              Métrologie
            </TabButton>
            <TabButton active={tab === 'documents'} onClick={() => setTab('documents')}>
              <FileText className="w-3.5 h-3.5 inline mr-1" />
              Documents
            </TabButton>
            <TabButton active={tab === 'qr'} onClick={() => setTab('qr')}>
              <QrCode className="w-3.5 h-3.5 inline mr-1" />
              QR
            </TabButton>
            <TabButton active={tab === 'historique'} onClick={() => setTab('historique')}>
              <History className="w-3.5 h-3.5 inline mr-1" />
              Historique
            </TabButton>
          </nav>
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          {errorMessage && (
            <div className="rounded-lg border border-red-200 bg-red-50 p-3 flex items-start space-x-2 mb-4">
              <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-700">{errorMessage}</p>
            </div>
          )}

          {loading || !detail ? (
            <div className="flex items-center justify-center p-12">
              <Loader2 className="w-8 h-8 animate-spin text-primary-500" />
            </div>
          ) : tab === 'info' ? (
            <div ref={pdfRef}>
              <InfoTab
                detail={detail}
                canEdit={canEdit}
                onAskUser={() => setAskUserOpen(true)}
                onOpenService={setOpenServiceId}
                onOpenBatiment={setOpenBatimentId}
                onOpenResponsable={(id) => {
                  onClose();
                  navigate(`/personnels/${id}`);
                }}
              />
            </div>
          ) : tab === 'affectations' ? (
            <AffectationsPanel
              equipementId={equipementId}
              onRefresh={() => {
                refresh();
                onRefresh?.();
              }}
            />
          ) : tab === 'maintenance' ? (
            <MaintenancesPanel equipementId={equipementId} />
          ) : tab === 'metrologie' ? (
            <EtalonnagesPanel equipementId={equipementId} />
          ) : tab === 'documents' ? (
            <DocumentsList entityType="equipement" entityId={equipementId} />
          ) : tab === 'qr' ? (
            <QrLabelPanel equipementId={equipementId} />
          ) : (
            <HistoriqueTimeline entityType="equipement" entityId={equipementId} />
          )}
        </div>
      </aside>

      {editOpen && (
        <EquipementFormModal
          equipementId={equipementId}
          onClose={() => setEditOpen(false)}
          onSaved={() => {
            setEditOpen(false);
            refresh();
            onRefresh?.();
          }}
        />
      )}

      {askUserOpen && (
        <DemanderAffectationModal
          equipementId={equipementId}
          onClose={() => setAskUserOpen(false)}
          onSaved={() => {
            setAskUserOpen(false);
            refresh();
            onRefresh?.();
          }}
        />
      )}

      {openServiceId && (
        <ServiceDetailDrawer serviceId={openServiceId} onClose={() => setOpenServiceId(null)} />
      )}

      {openBatimentId && (
        <BatimentDetailDrawer batimentId={openBatimentId} onClose={() => setOpenBatimentId(null)} />
      )}
    </ModalPortal>
  );
}

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick(): void;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      className={`px-3 py-2.5 text-xs sm:text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
        active
          ? 'text-primary-600 border-primary-500 bg-primary-50/50'
          : 'text-gray-600 border-transparent hover:text-gray-900'
      }`}
    >
      {children}
    </button>
  );
}

// ─────────────────────────────────────────────────────────────────────────────

function InfoTab({
  detail,
  canEdit,
  onAskUser,
  onOpenService,
  onOpenBatiment,
  onOpenResponsable,
}: {
  detail: EquipementDetail;
  canEdit: boolean;
  onAskUser(): void;
  onOpenService(serviceId: string): void;
  onOpenBatiment(batimentId: string): void;
  onOpenResponsable(personnelId: string): void;
}) {
  const localisation = [
    detail.batiment?.nom,
    detail.etage
      ? `Étage ${detail.etage.numero}${detail.etage.nom ? ` (${detail.etage.nom})` : ''}`
      : null,
    detail.salle?.nom,
  ]
    .filter(Boolean)
    .join(' › ');

  return (
    <div className="space-y-4">
      <section>
        <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
          Identité
        </h3>
        <dl className="text-sm space-y-1.5">
          <Row label="Code" value={detail.code_equipement} />
          <Row label="Désignation" value={detail.designation} />
          <Row label="N° inventaire" value={detail.numero_inventaire ?? '—'} />
          <Row label="Code-barres" value={detail.code_barres ?? '—'} />
          <Row label="N° série" value={detail.numero_serie ?? '—'} />
          <Row label="Fabricant" value={detail.fabricant ?? '—'} />
          <Row label="Modèle" value={detail.modele ?? '—'} />
          <Row label="Mobile" value={detail.est_mobile ? 'Oui' : 'Non'} />
        </dl>
      </section>

      <section>
        <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
          Classification
        </h3>
        <dl className="text-sm space-y-1.5">
          <Row label="Type" value={detail.type} />
          <Row label="Statut" value={<StatutEquipementBadge statut={detail.statut} />} />
          <Row
            label="Criticité"
            value={detail.criticite ? <CriticiteBadge criticite={detail.criticite} /> : '—'}
          />
        </dl>
      </section>

      <section>
        <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
          Localisation & rattachement
        </h3>
        <dl className="text-sm space-y-1.5">
          <Row
            label="Service"
            value={
              detail.service ? (
                <button
                  type="button"
                  onClick={() => onOpenService(detail.service!.id)}
                  className="inline-flex items-center text-primary-600 hover:text-primary-700 hover:underline"
                >
                  <Building className="w-3 h-3 mr-1" />
                  {detail.service.code} — {detail.service.nom_complet}
                </button>
              ) : (
                '—'
              )
            }
          />
          <Row
            label="Localisation"
            value={
              localisation ? (
                detail.batiment ? (
                  <button
                    type="button"
                    onClick={() => onOpenBatiment(detail.batiment!.id)}
                    className="inline-flex items-center text-primary-600 hover:text-primary-700 hover:underline text-right"
                  >
                    <MapPin className="w-3 h-3 mr-1 flex-shrink-0" />
                    {localisation}
                  </button>
                ) : (
                  <span className="inline-flex items-center">
                    <MapPin className="w-3 h-3 mr-1 text-gray-400" />
                    {localisation}
                  </span>
                )
              ) : (
                '—'
              )
            }
          />
          <Row
            label="Responsable"
            value={
              detail.responsable ? (
                <button
                  type="button"
                  onClick={() => onOpenResponsable(detail.responsable!.id)}
                  className="text-primary-600 hover:text-primary-700 hover:underline"
                >
                  {detail.responsable.first_name} {detail.responsable.last_name}
                </button>
              ) : (
                '—'
              )
            }
          />
        </dl>
      </section>

      <section>
        <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
          Affectation utilisateur
        </h3>
        <dl className="text-sm space-y-1.5">
          <Row label="Statut" value={renderStatutAff(detail.statut_affectation)} />
          <Row
            label="Affecté à"
            value={
              detail.utilisateur_affecte
                ? `${detail.utilisateur_affecte.first_name} ${detail.utilisateur_affecte.last_name}`
                : '—'
            }
          />
          {detail.date_demande_affectation && (
            <Row
              label="Demande"
              value={
                <span className="inline-flex items-center">
                  <Calendar className="w-3 h-3 mr-1 text-gray-400" />
                  {new Date(detail.date_demande_affectation).toLocaleString()}
                </span>
              }
            />
          )}
          {detail.motif_refus && (
            <Row
              label="Motif de refus"
              value={<span className="text-red-700">{detail.motif_refus}</span>}
            />
          )}
        </dl>
        {canEdit &&
          (detail.statut_affectation === 'non_affecte' ||
            detail.statut_affectation === 'refuse') && (
            <button
              onClick={onAskUser}
              className="mt-2 inline-flex items-center px-3 py-1.5 border border-primary-200 text-primary-700 bg-primary-50 hover:bg-primary-100 rounded-lg text-xs font-medium"
            >
              <Users className="w-3.5 h-3.5 mr-1" />
              Demander affectation à un utilisateur
            </button>
          )}
      </section>

      {Object.keys(detail.custom_fields ?? {}).length > 0 && (
        <section>
          <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
            Champs personnalisés
          </h3>
          <dl className="text-sm space-y-1.5">
            {Object.entries(detail.custom_fields).map(([k, v]) => (
              <Row key={k} label={k} value={String(v ?? '—')} />
            ))}
          </dl>
        </section>
      )}

      <section className="pt-2 border-t border-gray-100">
        <p className="text-xs text-gray-400">
          Créé le {new Date(detail.created_at).toLocaleString()}
          <br />
          Modifié le {new Date(detail.updated_at).toLocaleString()}
        </p>
      </section>
    </div>
  );
}

function renderStatutAff(s: EquipementStatutAffectation): React.ReactNode {
  const cfg: Record<EquipementStatutAffectation, string> = {
    non_affecte: 'bg-gray-100 text-gray-700 border-gray-200',
    en_attente_acceptation: 'bg-yellow-100 text-yellow-800 border-yellow-200',
    affecte: 'bg-green-100 text-green-800 border-green-200',
    refuse: 'bg-red-100 text-red-800 border-red-200',
  };
  return (
    <span
      className={`inline-flex items-center text-xs px-1.5 py-0.5 rounded font-medium border ${cfg[s]}`}
    >
      {s.replace(/_/g, ' ')}
    </span>
  );
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-2">
      <dt className="text-gray-500 text-xs">{label}</dt>
      <dd className="text-gray-900 text-right break-all">{value}</dd>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────

function DemanderAffectationModal({
  equipementId,
  onClose,
  onSaved,
}: {
  equipementId: string;
  onClose(): void;
  onSaved(): void;
}) {
  const [personnels, setPersonnels] = useState<
    Array<{ id: string; first_name: string; last_name: string }>
  >([]);
  const [userId, setUserId] = useState('');
  const [motif, setMotif] = useState('');
  const [saving, setSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    api
      .get<{ data: Array<{ id: string; first_name: string; last_name: string }> }>(
        '/personnels?status=active&page_size=200',
      )
      .then((res) => setPersonnels(res.data.data))
      .catch(() => setPersonnels([]));
  }, []);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!userId) {
      setErrorMessage('Sélectionnez un utilisateur.');
      return;
    }
    setSaving(true);
    setErrorMessage(null);
    try {
      await api.post(`/equipements/${equipementId}/demander-affectation`, {
        utilisateur_id: userId,
        motif,
      });
      onSaved();
    } catch (err) {
      setErrorMessage(extractErrorMessage(err));
    } finally {
      setSaving(false);
    }
  }

  return (
    <ModalPortal>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
        <div className="bg-white rounded-xl max-w-md w-full shadow-2xl">
          <div className="flex items-center justify-between p-4 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center">
              <ShieldAlert className="w-4 h-4 mr-2 text-primary-600" />
              Demander affectation utilisateur
            </h3>
            <button
              onClick={onClose}
              className="p-1 rounded-lg hover:bg-gray-100"
              aria-label="Fermer"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>
          <form onSubmit={submit} className="p-4 space-y-3">
            {errorMessage && (
              <div className="rounded-lg border border-red-200 bg-red-50 p-3 flex items-start space-x-2">
                <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-700">{errorMessage}</p>
              </div>
            )}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Utilisateur *</label>
              <select
                value={userId}
                onChange={(e) => setUserId(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                required
              >
                <option value="">— Sélectionnez —</option>
                {personnels.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.last_name} {p.first_name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Motif *</label>
              <textarea
                value={motif}
                onChange={(e) => setMotif(e.target.value)}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                required
              />
            </div>
            <p className="text-xs text-gray-500 italic">
              L'utilisateur recevra une notification et pourra accepter / refuser depuis sa page
              "Mes affectations".
            </p>
            <div className="flex justify-end space-x-2 pt-2">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
              >
                Annuler
              </button>
              <button
                type="submit"
                disabled={saving}
                className="inline-flex items-center px-4 py-2 rounded-lg shadow-sm text-sm font-medium text-white bg-primary-500 hover:bg-primary-600 disabled:opacity-50"
              >
                {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Envoyer la demande
              </button>
            </div>
          </form>
        </div>
      </div>
    </ModalPortal>
  );
}
